'use client';

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';

// ─── Audio tracks ─────────────────────────────────────────────────────────────
// Files live in packages/meet-core/public/audio/
// Next.js serves public/ from root, so src is just /audio/filename.mp3

interface AudioTrack {
  id:    string;
  title: string;
  src:   string;
}

const AUDIO_TRACKS: AudioTrack[] = [
  { id: 'track-01', title: 'the_void_is_calling.mp3',     src: '/audio/the_void_is_calling.mp3'     },
  { id: 'track-02', title: 'adrift.mp3',                  src: '/audio/adrift.mp3'                  },
  { id: 'track-03', title: 'ionosphere.mp3',              src: '/audio/ionosphere.mp3'              },
  { id: 'track-04', title: 'mysterious_transmission.mp3', src: '/audio/mysterious_transmission.mp3' },
  { id: 'track-05', title: 'chrome_cobra.mp3',            src: '/audio/chrome_cobra.mp3'            },
];

const AUDIO_DEFAULT_VOL = 0.5;
const AUDIO_FADE_MS     = 1800;
const ROOM_MINI_POS_KEY = 'vl_room_mp_pos';
const HUD_POS_KEY       = 'vl_hud_pos';
const ROOM_MINI_GAP     = 10;

interface OverlayPos {
  top: number;
  left: number;
}

function readStoredPos(key: string): OverlayPos | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.top === 'number' && typeof parsed?.left === 'number') {
      return { top: parsed.top, left: parsed.left };
    }
  } catch {}
  return null;
}

function writeStoredPos(key: string, pos: OverlayPos) {
  try { localStorage.setItem(key, JSON.stringify(pos)); } catch {}
}

function getMinTop(): number {
  return (window as any).electronAPI ? 40 : 4;
}

function clampOverlayPos(top: number, left: number, el: HTMLElement | null): OverlayPos {
  const minTop = getMinTop();
  const maxTop = el ? window.innerHeight - el.offsetHeight - 56 : window.innerHeight - 96;
  const maxLeft = el ? window.innerWidth - el.offsetWidth - 4 : window.innerWidth - 240;
  return {
    top: Math.max(minTop, Math.min(top, Math.max(minTop, maxTop))),
    left: Math.max(4, Math.min(left, Math.max(4, maxLeft))),
  };
}

function getHudAnchoredMiniPos(): OverlayPos {
  if (typeof document !== 'undefined') {
    const hud = document.getElementById('vl-room-hud');
    if (hud) {
      const rect = hud.getBoundingClientRect();
      return {
        top: Math.round(rect.bottom + ROOM_MINI_GAP),
        left: Math.round(rect.left),
      };
    }
  }
  const hudPos = readStoredPos(HUD_POS_KEY);
  if (hudPos) {
    return { top: hudPos.top + 88, left: hudPos.left };
  }
  return { top: 16, left: 16 };
}

// ─── AudioEngine interface ────────────────────────────────────────────────────

interface AudioEngineBase {
  playing:        boolean;
  trackIndex:     number;
  trackTitle:     string;
  volume:         number;
  setVolume:      (v: number) => void;
  toggle:         () => void;
  prev:           () => void;
  next:           () => void;
  fadeOutAndStop:  () => void;
}

export interface AudioEngine extends AudioEngineBase {
  // Mini-player visibility (managed by provider, exposed for /music command etc.)
  miniVisible:    boolean;
  showMini:       () => void;
  hideMini:       () => void;
  toggleMini:     () => void;
  // Set by VoidlineRoom on mount — tells provider to stop rendering mini-player
  // (VoidlineRoom renders its own in-room overlay instead)
  inRoom:         boolean;
  setInRoom:      (v: boolean) => void;
}

const AudioContext_ = createContext<AudioEngine | null>(null);

export function useAudio(): AudioEngine {
  const ctx = useContext(AudioContext_);
  if (!ctx) throw new Error('useAudio must be used within AudioProvider');
  return ctx;
}

// ─── Audio engine hook ────────────────────────────────────────────────────────

function useAudioEngine(): AudioEngineBase {
  const [trackIndex, setTrackIndex] = useState(() =>
    Math.floor(Math.random() * AUDIO_TRACKS.length),
  );
  const [playing, setPlaying] = useState(false);
  const [volume,  setVolumeState] = useState(AUDIO_DEFAULT_VOL);

  const elA       = useRef<HTMLAudioElement | null>(null);
  const elB       = useRef<HTMLAudioElement | null>(null);
  const active    = useRef<'A' | 'B'>('A');
  const fadeTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const volRef    = useRef(AUDIO_DEFAULT_VOL);
  const idxRef    = useRef(trackIndex);
  const playRef   = useRef(false);

  useEffect(() => { volRef.current = volume; }, [volume]);

  function getEls() {
    if (!elA.current) {
      elA.current = new Audio();
      elA.current.loop   = false;
      elA.current.volume = 0;
    }
    if (!elB.current) {
      elB.current = new Audio();
      elB.current.loop   = false;
      elB.current.volume = 0;
    }
    return { a: elA.current, b: elB.current };
  }

  function getActive()   { const { a, b } = getEls(); return active.current === 'A' ? a : b; }
  function getIncoming() { const { a, b } = getEls(); return active.current === 'A' ? b : a; }

  function stopFade() {
    if (fadeTimer.current) { clearInterval(fadeTimer.current); fadeTimer.current = null; }
  }

  function crossfadeTo(incoming: HTMLAudioElement, outgoing: HTMLAudioElement, targetVol: number) {
    stopFade();
    const steps    = 40;
    const interval = AUDIO_FADE_MS / steps;
    let   step     = 0;
    fadeTimer.current = setInterval(() => {
      step++;
      const t = step / steps;
      incoming.volume = Math.min(targetVol * t, 1);
      outgoing.volume = Math.max(targetVol * (1 - t), 0);
      if (step >= steps) {
        stopFade();
        outgoing.pause();
        outgoing.src    = '';
        outgoing.volume = 0;
      }
    }, interval);
  }

  // Helper: attempt play() with canplay fallback for cold-start reliability.
  // Calls onSuccess once audio is actually playing.
  function reliablePlay(el: HTMLAudioElement, onSuccess: () => void) {
    const p = el.play();
    if (p) {
      p.then(onSuccess).catch(() => {
        // play() rejected — element likely hasn't buffered yet.
        // Wait for canplay then retry once.
        function onCanPlay() {
          el.removeEventListener('canplay', onCanPlay);
          el.play().then(onSuccess).catch((e) =>
            console.warn('[VL audio] retry failed:', e),
          );
        }
        el.addEventListener('canplay', onCanPlay);
      });
    } else {
      onSuccess();
    }
  }

  const playTrack = useCallback((idx: number, fade = true) => {
    const { a, b } = getEls();
    const out = active.current === 'A' ? a : b;
    const inc = active.current === 'A' ? b : a;
    const vol = volRef.current;

    inc.src          = AUDIO_TRACKS[idx].src;
    inc.currentTime  = 0;
    inc.volume       = fade ? 0 : vol;

    // Flip active pointer + update refs/state optimistically.
    // reliablePlay handles retry if the browser hasn't buffered yet.
    active.current = active.current === 'A' ? 'B' : 'A';
    idxRef.current = idx;
    setTrackIndex(idx);

    reliablePlay(inc, () => {
      // Only mark as playing once audio is actually going
      playRef.current = true;
      setPlaying(true);
    });

    if (fade && out.src) {
      crossfadeTo(inc, out, vol);
    } else {
      stopFade();
      out.pause(); out.src = ''; out.volume = 0;
      inc.volume = vol;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onEnded() {
      if (!playRef.current) return;
      const next = (idxRef.current + 1) % AUDIO_TRACKS.length;
      playTrack(next, false);
    }
    const { a, b } = getEls();
    a.addEventListener('ended', onEnded);
    b.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('ended', onEnded);
      b.removeEventListener('ended', onEnded);
    };
  }, [playTrack]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback(() => {
    if (!playRef.current) {
      const cur = getActive();
      if (cur.src && cur.currentTime > 0) {
        // Resume a previously paused track (has valid position)
        cur.volume = volRef.current;
        reliablePlay(cur, () => {
          playRef.current = true;
          setPlaying(true);
        });
      } else {
        // Cold start or stale element — start fresh
        playTrack(idxRef.current, false);
      }
    } else {
      stopFade();
      getActive().pause();
      getIncoming().pause();
      playRef.current = false;
      setPlaying(false);
    }
  }, [playTrack]); // eslint-disable-line react-hooks/exhaustive-deps

  const prev = useCallback(() => {
    const idx = (idxRef.current - 1 + AUDIO_TRACKS.length) % AUDIO_TRACKS.length;
    playTrack(idx, true);
  }, [playTrack]);

  const next = useCallback(() => {
    const idx = (idxRef.current + 1) % AUDIO_TRACKS.length;
    playTrack(idx, true);
  }, [playTrack]);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    volRef.current = clamped;
    setVolumeState(clamped);
    const cur = getActive();
    if (cur.src) cur.volume = clamped;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fadeOutAndStop = useCallback(() => {
    if (!playRef.current) return;
    stopFade();
    const cur = getActive();
    const inc = getIncoming();
    const startVol = cur.volume || volRef.current;
    const steps = 50;
    const interval = 2000 / steps;  // 2s fade
    let step = 0;
    fadeTimer.current = setInterval(() => {
      step++;
      const t = step / steps;
      const v = startVol * (1 - t * t);  // ease-out curve
      cur.volume = Math.max(v, 0);
      if (step >= steps) {
        stopFade();
        cur.pause(); cur.src = ''; cur.volume = 0;
        inc.pause(); inc.src = ''; inc.volume = 0;
        playRef.current = false;
        setPlaying(false);
      }
    }, interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    playing,
    trackIndex,
    trackTitle: AUDIO_TRACKS[trackIndex].title,
    volume,
    setVolume,
    toggle,
    prev,
    next,
    fadeOutAndStop,
  };
}

// ─── Mini player (non-homepage) ───────────────────────────────────────────────

export interface MiniPlayerProps {
  audio: AudioEngine;
  position?: 'top-left' | 'below-hud';
  onClose?: () => void;
  roomName?: string;
}

const isArcNeon = (r: string) => r === 'the-arc-neon';

export function MiniPlayer({ audio, position = 'top-left', onClose, roomName }: MiniPlayerProps) {
  const inRoom = position === 'below-hud';
  const arc = roomName ? isArcNeon(roomName) : false;
  const playerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startMx: number; startMy: number; startTop: number; startLeft: number } | null>(null);
  const roomPosStoredRef = useRef<boolean>(!!(inRoom && readStoredPos(ROOM_MINI_POS_KEY)));

  // Collapse only available on pre-join (not in-room)
  const [collapsed, setCollapsed] = useState(() => {
    if (inRoom || typeof window === 'undefined') return false;
    return localStorage.getItem('vl_mp_collapsed') === '1';
  });
  const [roomPos, setRoomPos] = useState<OverlayPos | null>(() => {
    if (!inRoom) return null;
    return readStoredPos(ROOM_MINI_POS_KEY);
  });
  const [roomReady, setRoomReady] = useState(() => !inRoom);
  const [roomDragging, setRoomDragging] = useState(false);

  useEffect(() => {
    if (inRoom) return;
    const stored = localStorage.getItem('vl_mp_collapsed') === '1';
    setCollapsed(stored);
  }, [inRoom]);

  useEffect(() => {
    if (!inRoom) return;
    setRoomPos(prev => {
      const base = prev ?? getHudAnchoredMiniPos();
      const next = clampOverlayPos(base.top, base.left, playerRef.current);
      if (roomPosStoredRef.current) writeStoredPos(ROOM_MINI_POS_KEY, next);
      return next;
    });
    setRoomReady(true);
  }, [inRoom]);

  useEffect(() => {
    if (!inRoom || !roomReady) return;
    const onResize = () => {
      setRoomPos(prev => {
        const base = prev ?? getHudAnchoredMiniPos();
        const next = clampOverlayPos(base.top, base.left, playerRef.current);
        if (roomPosStoredRef.current) writeStoredPos(ROOM_MINI_POS_KEY, next);
        return next;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [inRoom, roomReady]);

  function toggleCollapse() {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('vl_mp_collapsed', next ? '1' : '0');
      return next;
    });
  }

  const css = inRoom ? MINI_ROOM_CSS : MINI_CSS;
  const themeClass = arc ? ' vl-mp-arc' : '';

  const handleRoomHeaderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!inRoom || !roomPos) return;
    if ((e.target as HTMLElement).closest('[data-no-room-drag]')) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      startMx: e.clientX,
      startMy: e.clientY,
      startTop: roomPos.top,
      startLeft: roomPos.left,
    };
    setRoomDragging(true);
  }, [inRoom, roomPos]);

  const handleRoomHeaderPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startMx;
    const dy = e.clientY - dragRef.current.startMy;
    setRoomPos(clampOverlayPos(
      dragRef.current.startTop + dy,
      dragRef.current.startLeft + dx,
      playerRef.current,
    ));
  }, []);

  const finishRoomDrag = useCallback((clientX: number, clientY: number) => {
    if (!dragRef.current) return;
    const dx = clientX - dragRef.current.startMx;
    const dy = clientY - dragRef.current.startMy;
    const next = clampOverlayPos(
      dragRef.current.startTop + dy,
      dragRef.current.startLeft + dx,
      playerRef.current,
    );
    dragRef.current = null;
    setRoomDragging(false);
    setRoomPos(next);
    roomPosStoredRef.current = true;
    writeStoredPos(ROOM_MINI_POS_KEY, next);
  }, []);

  const handleRoomHeaderPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    finishRoomDrag(e.clientX, e.clientY);
  }, [finishRoomDrag]);

  if (inRoom) {
    const fixedPos = roomPos ?? getHudAnchoredMiniPos();

    // In-room variant: spawns below the HUD, then moves independently.
    return (
      <>
        <style>{css}</style>
        <div
          ref={playerRef}
          className={`vl-mp-room${themeClass}`}
          style={{
            position: 'fixed',
            top: fixedPos.top,
            left: fixedPos.left,
            zIndex: 20,
            visibility: roomReady ? 'visible' : 'hidden',
          }}
        >
          {/* Row 1: filename + END */}
          <div
            className="vl-mp-r-header"
            onPointerDown={handleRoomHeaderPointerDown}
            onPointerMove={handleRoomHeaderPointerMove}
            onPointerUp={handleRoomHeaderPointerUp}
            onPointerCancel={handleRoomHeaderPointerUp}
            style={{ cursor: roomDragging ? 'grabbing' : 'grab' }}
          >
            <span className="vl-mp-r-track">{audio.trackTitle}</span>
            {onClose && (
              <button className="vl-mp-r-end" data-no-room-drag="true" onClick={onClose}>END</button>
            )}
          </div>
          {/* Row 2: transport + volume */}
          <div className="vl-mp-r-controls">
            <div className="vl-mp-r-transport">
              <button className="vl-mp-r-btn" onClick={audio.prev} aria-label="Previous">{'<<'}</button>
              <button className="vl-mp-r-btn vl-mp-r-play" onClick={audio.toggle} aria-label={audio.playing ? 'Pause' : 'Play'}>
                {audio.playing
                  ? <span className="vl-mp-r-pause"><span/><span/></span>
                  : '▶'}
              </button>
              <button className="vl-mp-r-btn" onClick={audio.next} aria-label="Next">{'>>'}</button>
            </div>
            <div className="vl-mp-r-vol-row">
              <span className="vl-mp-r-vol-label">VOL</span>
              <input
                className="vl-mp-r-vol"
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={audio.volume}
                onChange={(e) => audio.setVolume(parseFloat(e.target.value))}
                aria-label="Volume"
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  // Pre-join / standalone variant
  const posClass = 'vl-mp-topleft';

  return (
    <>
      <style>{css}</style>
      <div id="vl-mini-player" className={`${posClass}${collapsed ? ' vl-mp-collapsed' : ''}`}>
        {collapsed ? (
          <button
            className="vl-mp-bracket-btn"
            onClick={toggleCollapse}
            aria-label="Expand audio player"
          >{'[▶]'}</button>
        ) : (
          <>
            {/* Row 1: filename + stow/kill buttons */}
            <div className="vl-mp-header">
              <span className="vl-mp-track">{audio.trackTitle}</span>
              <div className="vl-mp-actions">
                <button
                  className="vl-mp-bracket-btn"
                  onClick={toggleCollapse}
                  aria-label="Collapse audio player"
                >{'[◀]'}</button>
                {onClose && (
                  <button
                    className="vl-mp-bracket-btn vl-mp-kill"
                    onClick={onClose}
                    aria-label="Close audio player"
                  >{'[×]'}</button>
                )}
              </div>
            </div>
            {/* Row 2: transport left, volume right */}
            <div className="vl-mp-controls">
              <div className="vl-mp-transport">
                <button className="vl-mp-btn" onClick={audio.prev} aria-label="Previous">{'<<'}</button>
                <button className="vl-mp-btn vl-mp-play" onClick={audio.toggle} aria-label={audio.playing ? 'Pause' : 'Play'}>
                  {audio.playing
                    ? <span className="vl-mp-pause-icon"><span/><span/></span>
                    : '▶'}
                </button>
                <button className="vl-mp-btn" onClick={audio.next} aria-label="Next">{'>>'}</button>
              </div>
              <div className="vl-mp-vol-row">
                <span className="vl-mp-vol-label">VOL</span>
                <input
                  className="vl-mp-vol"
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={audio.volume}
                  onChange={(e) => audio.setVolume(parseFloat(e.target.value))}
                  aria-label="Volume"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const MINI_CSS = `
#vl-mini-player {
  pointer-events:all;
  z-index:900;
  display:flex;
  flex-direction:column;
  background:rgba(0,8,12,0.88);
  backdrop-filter:blur(4px);
  -webkit-backdrop-filter:blur(4px);
  border:1px solid rgba(78,232,212,0.25);
  border-left:2px solid rgba(78,232,212,0.6);
  padding:6px 8px;
  font-family:'Share Tech Mono', monospace;
  box-shadow:
    -2px 0 8px rgba(78,232,212,0.2),
    0 2px 12px rgba(0,0,0,0.4);
  animation:vl-mp-fadein 0.3s ease forwards;
  width:240px;
}
/* ── Position variants ── */
#vl-mini-player.vl-mp-topleft {
  position:fixed;
  top:48px;
  left:28px;
}
#vl-mini-player.vl-mp-hud {
  position:relative;
}
@keyframes vl-mp-fadein {
  from { opacity:0; transform:translateY(-6px); }
  to   { opacity:1; transform:translateY(0); }
}
/* ── Collapsed state ── */
#vl-mini-player.vl-mp-collapsed {
  width:auto;
  padding:0;
  border:none;
  background:transparent;
  backdrop-filter:none;
  box-shadow:none;
}
/* ── Header: filename + action buttons ── */
.vl-mp-header {
  display:flex; align-items:center; gap:4px;
  padding-bottom:4px;
  border-bottom:1px solid rgba(78,232,212,0.08);
}
.vl-mp-track {
  font-size:9px; letter-spacing:0.08em;
  color:#8fffef;
  text-shadow:0 0 6px rgba(78,232,212,0.4);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  flex:1; min-width:0;
}
.vl-mp-actions {
  display:flex; align-items:center; gap:2px; flex-shrink:0; margin-left:auto;
}
/* ── Bracketed terminal buttons: [◀] [×] [▶] ── */
.vl-mp-bracket-btn {
  background:transparent; border:none;
  font-family:'Share Tech Mono', monospace;
  font-size:9px; letter-spacing:0.05em;
  color:rgba(78,232,212,0.45);
  cursor:pointer; padding:1px 2px; margin:0;
  transition:color 0.15s ease, text-shadow 0.15s ease;
  line-height:1; white-space:nowrap;
}
.vl-mp-bracket-btn:hover {
  color:#8fffef;
  text-shadow:0 0 8px rgba(78,232,212,0.7);
}
.vl-mp-bracket-btn.vl-mp-kill {
  color:rgba(232,78,58,0.45);
}
.vl-mp-bracket-btn.vl-mp-kill:hover {
  color:#e84e3a;
  text-shadow:0 0 8px rgba(232,78,58,0.7);
}
/* Collapsed: style the expand button as a standalone pill */
.vl-mp-collapsed .vl-mp-bracket-btn {
  background:rgba(0,8,12,0.88);
  border:1px solid rgba(78,232,212,0.3);
  border-left:2px solid rgba(78,232,212,0.6);
  color:#8fffef;
  font-size:9px;
  padding:5px 6px;
  display:flex; align-items:center; justify-content:center;
  text-shadow:0 0 8px rgba(78,232,212,0.6);
  box-shadow:-2px 0 8px rgba(78,232,212,0.2), 0 2px 12px rgba(0,0,0,0.4);
  backdrop-filter:blur(4px);
}
.vl-mp-collapsed .vl-mp-bracket-btn:hover {
  border-color:rgba(78,232,212,0.6);
  text-shadow:0 0 14px rgba(78,232,212,0.9);
}
/* ── Controls row: transport + volume ── */
.vl-mp-controls {
  display:flex; align-items:center; gap:2px;
  padding-top:4px;
}
.vl-mp-transport {
  display:flex; align-items:center; flex-shrink:0;
}
.vl-mp-btn {
  background:transparent; border:none;
  font-family:'Share Tech Mono', monospace;
  color:#4ee8d4; cursor:pointer;
  padding:2px 4px; margin:0;
  font-size:11px; letter-spacing:-0.05em;
  text-shadow:0 0 6px rgba(78,232,212,0.4);
  transition:color 0.12s ease;
  line-height:1;
}
.vl-mp-btn:hover { color:#8fffef; }
/* Play/pause — fixed width to prevent layout jump */
.vl-mp-play {
  font-size:12px; width:18px;
  display:inline-flex; align-items:center; justify-content:center;
  color:#8fffef; text-shadow:0 0 10px rgba(78,232,212,0.9);
}
.vl-mp-pause-icon {
  display:inline-flex; align-items:center; gap:2px; height:12px;
}
.vl-mp-pause-icon span {
  display:block; width:2.5px; height:10px;
  background:#8fffef; box-shadow:0 0 6px rgba(78,232,212,0.8);
  border-radius:1px;
}
/* ── Volume — right side of controls row ── */
.vl-mp-vol-row {
  display:flex; align-items:center; gap:5px;
  flex:1; min-width:0;
  padding-left:6px;
  border-left:1px solid rgba(78,232,212,0.1);
}
.vl-mp-vol-label {
  font-size:7px; letter-spacing:0.3em; color:rgba(78,232,212,0.6);
  text-transform:uppercase; flex-shrink:0; user-select:none;
}
.vl-mp-vol {
  -webkit-appearance:none; appearance:none;
  flex:1; height:2px;
  background:rgba(78,232,212,0.18);
  border-radius:2px; outline:none; cursor:pointer;
  margin-right:2px;
}
.vl-mp-vol::-webkit-slider-thumb {
  -webkit-appearance:none; appearance:none;
  width:8px; height:8px; border-radius:50%;
  background:#8fffef;
  box-shadow:0 0 4px rgba(78,232,212,0.8);
  cursor:pointer;
}
.vl-mp-vol::-moz-range-thumb {
  width:8px; height:8px; border-radius:50%; border:none;
  background:#8fffef;
  box-shadow:0 0 4px rgba(78,232,212,0.8);
  cursor:pointer;
}
`;

// ─── In-room mini-player CSS (matches HUD element) ───────────────────────────

const MINI_ROOM_CSS = `
.vl-mp-room {
  --mp-fg:     #4ee8d4;
  --mp-fg-b:   #8fffef;
  --mp-fg-lo:  rgba(78,232,212,0.18);
  --mp-fg-glo: rgba(78,232,212,0.5);
  --mp-red:    #ff3030;
  --mp-red-lo: rgba(255,48,48,0.2);
  --mp-red-gl: rgba(255,48,48,0.4);

  pointer-events:all;
  display:flex; flex-direction:column;
  background:rgba(0,0,0,0.65);
  backdrop-filter:blur(4px);
  -webkit-backdrop-filter:blur(4px);
  border-radius:4px;
  padding:8px 12px 8px 10px;
  max-width:320px;
  font-family:'JetBrains Mono','Courier New',monospace;
  animation:vl-mpr-in 0.3s ease forwards;
}
/* Arc-neon theme override */
.vl-mp-room.vl-mp-arc {
  --mp-fg:     #ff4fa3;
  --mp-fg-b:   #ffb347;
  --mp-fg-lo:  rgba(255,79,163,0.18);
  --mp-fg-glo: rgba(255,79,163,0.55);
}
@keyframes vl-mpr-in {
  from { opacity:0; transform:translateY(-4px); }
  to   { opacity:1; transform:translateY(0); }
}
/* Header: filename + END button */
.vl-mp-r-header {
  display:flex; align-items:center; gap:6px;
  padding-bottom:5px;
  border-bottom:1px solid var(--mp-fg-lo);
}
.vl-mp-r-track {
  font-size:9px; letter-spacing:0.08em;
  color:var(--mp-fg-b);
  text-shadow:0 0 6px var(--mp-fg-glo);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  flex:1; min-width:0;
}
.vl-mp-r-end {
  background:none; cursor:pointer;
  font-family:'JetBrains Mono','Courier New',monospace;
  font-size:8px; letter-spacing:0.15em;
  text-transform:uppercase;
  color:var(--mp-red);
  text-shadow:0 0 8px var(--mp-red-gl);
  border:1px solid var(--mp-red-lo);
  border-radius:2px;
  padding:2px 8px;
  flex-shrink:0;
  transition:color 0.15s, border-color 0.15s, text-shadow 0.15s;
  white-space:nowrap;
}
.vl-mp-r-end:hover {
  color:#ff6060;
  border-color:rgba(255,48,48,0.5);
  text-shadow:0 0 12px rgba(255,48,48,0.7);
}
/* Controls row */
.vl-mp-r-controls {
  display:flex; align-items:center; gap:2px;
  padding-top:5px;
}
.vl-mp-r-transport { display:flex; align-items:center; flex-shrink:0; }
.vl-mp-r-btn {
  background:transparent; border:none;
  font-family:'JetBrains Mono','Courier New',monospace;
  color:var(--mp-fg); cursor:pointer;
  padding:2px 4px; margin:0;
  font-size:11px; letter-spacing:-0.05em;
  text-shadow:0 0 6px var(--mp-fg-glo);
  transition:color 0.12s ease; line-height:1;
}
.vl-mp-r-btn:hover { color:var(--mp-fg-b); }
.vl-mp-r-play {
  font-size:12px; width:18px;
  display:inline-flex; align-items:center; justify-content:center;
  color:var(--mp-fg-b);
  text-shadow:0 0 10px var(--mp-fg-glo);
}
.vl-mp-r-pause {
  display:inline-flex; align-items:center; gap:2px; height:12px;
}
.vl-mp-r-pause span {
  display:block; width:2.5px; height:10px;
  background:var(--mp-fg-b);
  box-shadow:0 0 6px var(--mp-fg-glo);
  border-radius:1px;
}
/* Volume */
.vl-mp-r-vol-row {
  display:flex; align-items:center; gap:5px;
  flex:1; min-width:0;
  padding-left:6px;
  border-left:1px solid var(--mp-fg-lo);
}
.vl-mp-r-vol-label {
  font-size:7px; letter-spacing:0.3em; color:var(--mp-fg);
  opacity:0.6;
  text-transform:uppercase; flex-shrink:0; user-select:none;
}
.vl-mp-r-vol {
  -webkit-appearance:none; appearance:none;
  width:0; flex:1; height:2px;
  background:var(--mp-fg-lo);
  border-radius:2px; outline:none; cursor:pointer;
  margin-right:2px;
}
.vl-mp-r-vol::-webkit-slider-thumb {
  -webkit-appearance:none; appearance:none;
  width:8px; height:8px; border-radius:50%;
  background:var(--mp-fg-b);
  box-shadow:0 0 4px var(--mp-fg-glo);
  cursor:pointer;
}
.vl-mp-r-vol::-moz-range-thumb {
  width:8px; height:8px; border-radius:50%; border:none;
  background:var(--mp-fg-b);
  box-shadow:0 0 4px var(--mp-fg-glo);
  cursor:pointer;
}
`;

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AudioProvider({ children }: { children: React.ReactNode }) {
  const engine   = useAudioEngine();
  const pathname = usePathname();
  const isHome   = pathname === '/';
  const isRoom   = pathname.startsWith('/rooms/');

  // Mini-player visibility state
  const [miniVisible, setMiniVisible] = useState(false);
  // inRoom — set by VoidlineRoom on mount; provider skips rendering, room handles it
  const [inRoom, setInRoom] = useState(false);

  // When route changes to a room page, show mini-player (for pre-join).
  // Reset inRoom when leaving room routes.
  useEffect(() => {
    if (isRoom) { setMiniVisible(true); }
    else if (!isHome) { setMiniVisible(true); }
    else { setMiniVisible(false); }
    if (!isRoom) setInRoom(false);
  }, [isRoom, isHome]);

  const showMini   = useCallback(() => setMiniVisible(true), []);
  const hideMini   = useCallback(() => setMiniVisible(false), []);
  const toggleMini = useCallback(() => setMiniVisible(v => !v), []);

  // Wrap fadeOutAndStop to also hide the mini-player when fade completes
  const fadeOutAndStop = useCallback(() => {
    engine.fadeOutAndStop();
    // Hide after fade duration (2s) + small buffer
    setTimeout(() => setMiniVisible(false), 2200);
  }, [engine]);

  const audio: AudioEngine = {
    ...engine,
    fadeOutAndStop,
    miniVisible,
    showMini,
    hideMini,
    toggleMini,
    inRoom,
    setInRoom,
  };

  // Render mini-player from provider except:
  // - Homepage (full SignalPanel handles it)
  // - When VoidlineRoom has mounted (it renders its own HUD-adjacent overlay)
  const renderMini = !isHome && miniVisible && !inRoom;

  return (
    <AudioContext_.Provider value={audio}>
      {children}
      {renderMini && (
        <MiniPlayer audio={audio} position="top-left" />
      )}
    </AudioContext_.Provider>
  );
}
